import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Chip,
  Box,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useGlobalConfig } from 'context/GlobalContext';
import { PilotApis, PendingUatConfig } from 'apis/pilotAPI';

interface PushUpdatesDialogProps {
  open: boolean;
  onClose: () => void;
  environmentName: string;
  projectId: string;
  onPushed?: (summary: any) => void;
}

export default function PushUpdatesDialog(props: PushUpdatesDialogProps) {
  const { open, onClose, environmentName, projectId, onPushed } = props;
  const { rc } = useGlobalConfig();

  const [pending, setPending] = useState<PendingUatConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!open || !projectId) return;
      setIsLoading(true);
      try {
        const resp = await PilotApis.getPendingUatConfigs(rc, projectId);
        setPending(resp.success && resp.data ? resp.data : []);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [open, projectId, rc]);

  const [localRows, setLocalRows] = useState<Array<{ id: string; configKey: string; configLevel: string; originalValue: string; currentValue: string }>>([]);

  useEffect(() => {
    if (open) {
      const source = Array.isArray(pending) ? pending : [];
      setLocalRows(source.map(p => ({
        id: p.id,
        configKey: p.configKey,
        configLevel: p.configLevel,
        originalValue: String(p.pingpong_value ?? ''),
        currentValue: String(p.db_value ?? '')
      })));
    }
  }, [open, pending]);

  const hasRows = localRows.length > 0;

  const handleRemove = async (id: string) => {
    if (!projectId) return;
    const source = Array.isArray(pending) ? pending : [];
    const src = source.find(p => p.id === id);
    if (!src) {
      // Remove locally if we can't find the source
      setLocalRows(prev => prev.filter(r => r.id !== id));
      return;
    }
    setIsApplying(true);
    try {
      const payload = [{
        project_id: projectId,
        config_key: src.configKey,
        config_type: src.configType || 'CUSTOM',
        config_value: String(src.pingpong_value ?? ''), // revert to old value
        config_level: src.configLevel || 'pilot',
        config_documentation: src.configDocumentation || '',
        config_regex: '.*',
        config_data_type: src.configDataType || '',
        display_name: src.configKey,
        features: (src as any).component || 'ingestion',
        editable: true,
        is_required: true
      }];
      const saveResp = await PilotApis.saveProjectConfigs(rc, payload as any);
      if (saveResp.success) {
        setLocalRows(prev => prev.filter(r => r.id !== id));
        setPending(prev => (Array.isArray(prev) ? prev.filter(p => p.id !== id) : prev));
      }
    } finally {
      setIsApplying(false);
    }
  };
  const handleChange = (id: string, value: string) => setLocalRows(prev => prev.map(r => r.id === id ? { ...r, currentValue: value } : r));

  const handleApply = async () => {
    if (!projectId) return;
    setIsApplying(true);
    try {
      // Build Save All payload: edited rows use currentValue; removed rows revert to originalValue
      const keptIds = new Set(localRows.map(r => r.id));
      const source = Array.isArray(pending) ? pending : [];
      const removed = source.filter(p => !keptIds.has(p.id));

      const payload = [
        // kept edits
        ...localRows.map(r => {
          const src = source.find(p => p.id === r.id)!;
          return {
            project_id: projectId,
            config_key: src.configKey,
            config_type: src.configType || 'CUSTOM',
            config_value: String(r.currentValue ?? ''),
            config_level: src.configLevel || 'pilot',
            config_documentation: src.configDocumentation || '',
            config_regex: '.*',
            config_data_type: src.configDataType || '',
            display_name: src.configKey,
            features: (src as any).component || 'ingestion',
            editable: true,
            is_required: true
          };
        }),
        // removed -> revert to original
        ...removed.map(src => ({
          project_id: projectId,
          config_key: src.configKey,
          config_type: src.configType || 'CUSTOM',
          config_value: String(src.pingpong_value ?? ''),
          config_level: src.configLevel || 'pilot',
          config_documentation: src.configDocumentation || '',
          config_regex: '.*',
          config_data_type: src.configDataType || '',
          display_name: src.configKey,
          features: (src as any).component || 'ingestion',
          editable: true,
          is_required: true
        }))
      ];

      if (payload.length > 0) {
        const saveResp = await PilotApis.saveProjectConfigs(rc, payload as any);
        if (!saveResp.success) {
          return;
        }
      }

      const pushResp = await PilotApis.pushConfigsToUat(rc, projectId);
      if (pushResp.success) {
        onPushed?.(pushResp.data);
      }
      onClose();
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Push Configuration Updates</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2, color: 'text.secondary' }}>
          Review pending configuration changes for <b>{environmentName}</b>. Remove any entries to revert them to their original values before pushing.
        </Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Config Key</TableCell>
                <TableCell>Original Value</TableCell>
                <TableCell>New Value</TableCell>
                <TableCell>Level</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasRows ? (
                localRows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Tooltip title="Remove from push (revert to original)">
                        <span>
                          <IconButton size="small" onClick={() => handleRemove(row.id)} disabled={isApplying}>
                            <DeleteOutlineIcon fontSize="small" color="error" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ 
                      fontFamily: 'monospace',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '200px'
                    }}>{row.configKey}</TableCell>
                    <TableCell sx={{ 
                      fontFamily: 'monospace', 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '200px'
                    }}>{row.originalValue}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.currentValue}
                        onChange={(e) => handleChange(row.id, e.target.value)}
                        inputProps={{ style: { fontFamily: 'monospace' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.configLevel} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                    No pending configuration updates.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isApplying}>Cancel</Button>
        <Button onClick={handleApply} disabled={!hasRows || isApplying || isLoading} variant="contained" startIcon={isApplying ? <CircularProgress size={16} /> : undefined}>
          {isApplying ? 'Processing…' : 'Update Configs'}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 